import { localStorageApiConnector } from 'apiConnector/local-torage';
import { isNavigatingInTradeBlotter } from 'epic/utils/navigation/navigation';
import { ofType } from 'redux-observable';
import { catchError, EMPTY, map, merge, Observable, of, switchMap, take, withLatestFrom, zip } from 'rxjs';
import { filter, ignoreElements, mergeMap, skipUntil } from 'rxjs/operators';
import { Action } from 'state/actions';
import {
  tradesBlottersFiltersLoaded,
  tradesBlottersFiltersLoadingFailed,
  tradesBlottersGlobalFiltersLoaded,
  tradesBlottersGlobalFiltersLoadingFailed,
} from 'state/blotters/trades/tradesBlotters.actions';
import { initialTradesBlottersFilters } from 'state/blotters/trades/tradesBlotters.model';
import {
  selectAllTradesBlottersFiltersByBlotterType,
  selectTradesBlottersGlobalFilters,
} from 'state/blotters/trades/tradesBlotters.selector';
import {
  AllTradeBlottersFiltersByBlotterType,
  GlobalFilters,
  TradeBlottersFiltersByBlotterType,
} from 'state/blotters/trades/types/filters';
import { PAGE_FILTER_OR_PREFERENCE_BLOTTER_TYPES } from 'state/blotters/trades/types/types';
import { EpicWithThunk } from 'state/state.common';

export function loadTradesBlottersFiltersEpic(): EpicWithThunk[] {
  return PAGE_FILTER_OR_PREFERENCE_BLOTTER_TYPES.map(blotterType => {
    return (actions$, _state$, { frontLogger, localStorageApiConnector }) => {
      const filterMatching$: Observable<Action> = actions$.pipe(
        ofType('TRADES_BLOTTERS_FILTER_ENABLED'),
        filter(action => action.blotter === blotterType),
      );

      const globalFilterLoaded$ = actions$.pipe(
        ofType('TRADES_BLOTTERS_GLOBAL_FILTERS_LOADED'),
        filter(action => action.filters.blotterType === blotterType),
        take(1),
      );

      const globalFilterLoadingFailed$ = actions$.pipe(
        ofType('TRADES_BLOTTERS_GLOBAL_FILTERS_LOADING_FAILED'),
        filter(_ => blotterType === initialTradesBlottersFilters.global.blotterType),
        take(1),
      );

      const navigateIn$ = merge(filterMatching$, globalFilterLoaded$, globalFilterLoadingFailed$);

      const api = localStorageApiConnector(
        `trades-blotters-filters-${blotterType}`,
        initialTradesBlottersFilters.blotters[blotterType],
      );

      return zip(actions$.pipe(ofType('USER_CURRENT_RECEIVED'), take(1)), navigateIn$).pipe(
        switchMap(_ => {
          return api.load<TradeBlottersFiltersByBlotterType<typeof blotterType>>().pipe(
            map(blotterFilters => tradesBlottersFiltersLoaded(blotterType, blotterFilters)),
            catchError((error: Error) => {
              frontLogger.error('An error occurred inside loadFilters {@FrontError}', error);
              return of(tradesBlottersFiltersLoadingFailed(blotterType));
            }),
          );
        }),
      );
    };
  });
}

export function persistTradesBlottersFiltersEpic(): EpicWithThunk[] {
  return PAGE_FILTER_OR_PREFERENCE_BLOTTER_TYPES.map(blotterType => {
    return (action$, state$, { frontLogger }) => {
      const filterLoaded$ = action$.pipe(
        ofType('TRADES_BLOTTERS_FILTERS_LOADED', 'TRADES_BLOTTERS_FILTERS_LOADING_FAILED'),
        filter(action => action.blotter === blotterType),
      );

      return action$.pipe(
        ofType(
          'TRADES_BLOTTERS_FILTER_CHANGED',
          'TRADES_BLOTTERS_FILTER_ENABLED',
          'TRADES_BLOTTERS_ADVANCED_FILTERS_TOGGLED',
        ),
        filter(action => action.blotter === blotterType),
        withLatestFrom(state$),
        skipUntil(filterLoaded$),
        switchMap(([_, state]) => {
          const filters: Required<AllTradeBlottersFiltersByBlotterType<typeof blotterType>> =
            selectAllTradesBlottersFiltersByBlotterType(state, blotterType);
          const persistBlotterFilters$ = localStorageApiConnector(
            `trades-blotters-filters-${blotterType}`,
            initialTradesBlottersFilters.blotters[blotterType],
          )
            .save(filters)
            .pipe(
              ignoreElements(),
              catchError((error: Error) => {
                frontLogger.error('An error occurred inside persistFilters {@FrontError}', error);
                return EMPTY;
              }),
            );

          return persistBlotterFilters$;
        }),
      );
    };
  });
}

export function loadTradesBlottersGlobalFiltersEpic(): EpicWithThunk {
  return (actions$, _, { frontLogger, localStorageApiConnector }) => {
    const api = localStorageApiConnector(`trades-blotters-filters`, initialTradesBlottersFilters.global);

    return actions$.pipe(
      filter(isNavigatingInTradeBlotter),
      take(1),
      mergeMap(_ => {
        return api.load<GlobalFilters>().pipe(
          map(filters => tradesBlottersGlobalFiltersLoaded(filters)),
          catchError((error: Error) => {
            frontLogger.error('An error occurred inside global load filter {@FrontError}', error);
            return of(tradesBlottersGlobalFiltersLoadingFailed());
          }),
        );
      }),
    );
  };
}

export function persistTradesBlottersGlobalFiltersEpic(): EpicWithThunk {
  return (action$, state$, { frontLogger }) =>
    action$.pipe(
      ofType(
        'TRADES_BLOTTERS_FILTER_CHANGED',
        'TRADES_BLOTTERS_FILTER_ENABLED',
        'TRADES_BLOTTERS_ADVANCED_FILTERS_TOGGLED',
      ),
      withLatestFrom(state$),
      switchMap(([_, state]) => {
        const globalFilters = selectTradesBlottersGlobalFilters(state);
        const api = localStorageApiConnector(`trades-blotters-filters`, initialTradesBlottersFilters.global);

        const persistGlobalFilters$ = api.save(globalFilters).pipe(
          ignoreElements(),
          catchError((error: Error) => {
            frontLogger.error('An error occurred inside persistGlobalFilters {@FrontError}', error);
            return EMPTY;
          }),
        );
        return persistGlobalFilters$;
      }),
    );
}
